import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => setMenuOpen(false), [location]);

  const scrollToSection = (selector: string) => {
    setMenuOpen(false);
    if (!isHome) {
      window.location.href = `/${selector}`;
      return;
    }
    setTimeout(() => {
      document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const navBg = scrolled || !isHome || menuOpen
    ? 'rgba(5, 5, 7, 0.95)'
    : 'transparent';

  return (
    <>
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9000,
        padding: '1rem 5%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: navBg,
        backdropFilter: scrolled || !isHome ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled || !isHome ? 'blur(20px)' : 'none',
        borderBottom: scrolled || !isHome
          ? '1px solid rgba(255,255,255,0.05)'
          : '1px solid transparent',
        transition: 'background 0.4s ease, border-color 0.4s ease, backdrop-filter 0.4s ease',
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
          <img
            src="/lrc_logo.webp"
            alt="LRC Digital"
            width="467"
            height="219"
            fetchPriority="high"
            style={{ height: '32px', width: 'auto', filter: 'drop-shadow(0 0 6px rgba(124, 168, 176,0.2))' }}
          />
          <span style={{
            fontFamily: 'var(--font-raleway)',
            fontWeight: 800,
            fontSize: '0.8rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(169,167,175,0.7)',
          }}>Digital</span>
        </Link>

        {/* Desktop Links */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2.5rem',
        }} className="nav-desktop">
          {isHome && (
            <>
              <button onClick={() => scrollToSection('.section-video-portal')} style={ghostNavBtn}>
                The Framework
              </button>
              <button onClick={() => scrollToSection('.section-portfolio')} style={ghostNavBtn}>
                Results
              </button>
            </>
          )}
          <Link to="/blog" style={{
            ...ghostNavBtn,
            color: location.pathname === '/blog' ? '#a2cca9' : 'rgba(169,167,175,0.8)',
            textDecoration: 'none',
          }}>
            Flow Theory
          </Link>
          <Link to="/apps" style={{
            ...ghostNavBtn,
            color: location.pathname === '/apps' ? '#a2cca9' : 'rgba(169,167,175,0.8)',
            textDecoration: 'none',
          }}>
            SaaS Apps
          </Link>
          <button
            onClick={() => scrollToSection('.section-diagnostic')}
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '0.78rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#050507',
              background: '#a2cca9',
              border: 'none',
              padding: '0.6rem 1.4rem',
              borderRadius: '2px',
              cursor: 'pointer',
              fontWeight: 600,
              transition: 'opacity 0.3s ease, box-shadow 0.3s ease',
              boxShadow: '0 0 16px rgba(162, 204, 169,0.3)',
            }}
            onMouseOver={e => (e.currentTarget.style.opacity = '0.85')}
            onMouseOut={e => (e.currentTarget.style.opacity = '1')}
          >
            Begin Diagnostic
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMenuOpen(o => !o)}
          className="nav-hamburger"
          aria-label="Toggle menu"
          style={{
            display: 'none',
            flexDirection: 'column',
            gap: '5px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
          }}
        >
          {[0, 1, 2].map(i => (
            <span key={i} style={{
              display: 'block',
              width: '22px',
              height: '1.5px',
              background: menuOpen && i === 1 ? 'transparent' : '#f4f1ea',
              transform: menuOpen
                ? i === 0 ? 'rotate(45deg) translate(4px, 4px)'
                  : i === 2 ? 'rotate(-45deg) translate(4px, -4px)'
                  : 'none'
                : 'none',
              transition: 'all 0.3s ease',
            }} />
          ))}
        </button>
      </nav>

      {/* Mobile Menu */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 8999,
        background: 'rgba(5,5,7,0.98)',
        backdropFilter: 'blur(30px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2.5rem',
        opacity: menuOpen ? 1 : 0,
        pointerEvents: menuOpen ? 'all' : 'none',
        transition: 'opacity 0.4s ease',
      }}>
        {isHome && (
          <>
            <MobileNavItem onClick={() => scrollToSection('.section-video-portal')}>The Framework</MobileNavItem>
            <MobileNavItem onClick={() => scrollToSection('.section-portfolio')}>Results</MobileNavItem>
            <MobileNavItem onClick={() => scrollToSection('.section-founder')}>About</MobileNavItem>
          </>
        )}
        <Link to="/blog" style={{ fontFamily: 'var(--font-serif)', fontSize: '2.2rem', color: location.pathname === '/blog' ? '#a2cca9' : '#f4f1ea', textDecoration: 'none', fontStyle: 'italic' }}>
          Flow Theory
        </Link>
        <Link to="/apps" style={{ fontFamily: 'var(--font-serif)', fontSize: '2.2rem', color: location.pathname === '/apps' ? '#a2cca9' : '#f4f1ea', textDecoration: 'none', fontStyle: 'italic' }}>
          SaaS Apps
        </Link>
        <button
          onClick={() => scrollToSection('.section-diagnostic')}
          style={{
            marginTop: '1rem',
            fontFamily: 'var(--font-sans)',
            fontSize: '0.85rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#050507',
            background: '#a2cca9',
            border: 'none',
            padding: '1rem 2.5rem',
            borderRadius: '2px',
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          Begin Diagnostic
        </button>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .nav-desktop { display: none !important; }
          .nav-hamburger { display: flex !important; }
        }
      `}</style>
    </>
  );
};

const ghostNavBtn: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: '0.78rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: 'rgba(169,167,175,0.8)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  transition: 'color 0.3s ease',
};

const MobileNavItem = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => (
  <button onClick={onClick} style={{
    fontFamily: 'var(--font-serif)',
    fontSize: '2.2rem',
    color: '#f4f1ea',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontStyle: 'italic',
    fontWeight: 400,
  }}>
    {children}
  </button>
);

export default Nav;
